import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa_install_dismissed';

export default function FloatingInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setVisible(false);
    });

    // Also show for iOS / Chinese phones (manual install)
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|android|huawei|xiaomi|oppo|vivo|miui|redmi/.test(ua);
    if (isMobile) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstalling(false);
      if (outcome === 'accepted') {
        setInstalled(true);
        setVisible(false);
      }
      setDeferredPrompt(null);
    } else {
      // No native prompt — show tooltip with manual guide
      setShowTooltip((prev) => !prev);
    }
  };

  if (!visible || installed) return null;

  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isHuawei = /huawei|honor/.test(ua);
  const isXiaomi = /xiaomi|miui|redmi/.test(ua);

  let guideText = '点击浏览器菜单 → 添加到桌面';
  if (isIOS) guideText = '点击底部分享按钮 → 添加到主屏幕';
  else if (isHuawei) guideText = '点击右上角菜单 → 添加到桌面';
  else if (isXiaomi) guideText = '点击右上角菜单 → 添加到桌面';

  return (
    <div className="fixed bottom-24 right-4 z-[9998] flex flex-col items-end gap-2">
      {/* Tooltip guide */}
      {showTooltip && (
        <div className="bg-gray-900 text-white text-xs rounded-xl px-4 py-3 max-w-[200px] text-right leading-relaxed">
          <p className="font-bold mb-1 text-amber-400">安装到桌面</p>
          <p>{guideText}</p>
          <button
            onClick={() => setShowTooltip(false)}
            className="mt-2 text-gray-400 hover:text-white cursor-pointer text-xs"
          >
            知道了
          </button>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={handleClick}
        className="group relative w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 transition-all cursor-pointer flex items-center justify-center"
        style={{ boxShadow: '0 4px 20px rgba(192,57,43,0.45)' }}
        title="安装到桌面"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-30 pointer-events-none"></span>

        {installing ? (
          <i className="ri-loader-4-line text-white text-xl animate-spin"></i>
        ) : (
          <i className="ri-download-line text-white text-xl"></i>
        )}

        {/* Label badge */}
        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-full px-2.5 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          安装App
        </span>
      </button>
    </div>
  );
}
